import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
    ACCOUNT_DETAILS_PAGE_URL,
    ALERTS_PAGE_URL,
    BILLS_PAGE_URL,
    GOMO_WORLD_URL,
    PAYMENTS_PAGE_URL,
    PROFILE_PAGE_URL,
    USAGE_PAGE_URL,
    WORDPRESS_URL,
} from "@constants";
import "./css/HomePage.scss";
import { useHomePage } from "./hook/useHomePage";
import { ESimBanner } from "@features/ManageSim/ESimBanner";
import {
    Badge,
    Card,
    CardContent,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    SvgIcon,
    Typography,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { OrderHandler } from "@features/Order/handler/OrderHandler";
import { Appointment } from "@features/Appointment/Appointment";
import { RecommendationBannerRemovalModal } from "@features/RecommendationBannerRemovalModal/RecommendationBannerRemovalModal";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { HomeContext } from "@context/HomeContext";
import { useStyle } from "./style";
import { CardIcon } from "@common/Icon/CardIcon/CardIcon";
import { AlertIcon } from "@common/Icon/AlertIcon/AlertIcon";
import { BillIcon } from "@common/Icon/BillIcon/BillIcon";
import { UsageIcon } from "@common/Icon/UsageIcon/UsageIcon";
import { UserIcon } from "@common/Icon/UserIcon/UserIcon";
import { AccountIcon } from "@common/Icon/AccountIcon/AccountIcon";
import { ContactIcon } from "@common/Icon/ContactIcon/ContactIcon";
import { TravelIcon } from "@common/Icon/TravelIcon/TravelIcon";
import { BroadbandTechSupportIcon } from "@common/Icon/BroadbandTechSupportIcon/BroadbandTechSupportIcon";
import { useSweeprOtc } from "./HomeMenuModal/hook/useSweeprOtc";
import { SweeprWidgetContent } from "./HomeMenuModal/SweeprWidgetContent/SweeprWidgetContent";
import { useFeatureFlag } from "@common/FeatureFlagProvider/useFeatureFlag";
import { featureFlagNameList } from "@common/FeatureFlag/FeatureFlagNameList";
import xHeader from "@static/img/header/x-header.svg";

interface IWelcomeMenuItem {
    action?: "openSweeprWidget";
    badge?: number;
    externalUrl?: string;
    icon: JSX.Element;
    label: string;
    url?: string;
}

export const HomePage = () => {
    const { t } = useTranslation(["navigation", "footer", "glossary", "sim", "common"]);
    const {
        appointmentList,
        backgroundHeight,
        count,
        homeRef,
        isLoading,
        mutate,
        recommendationBanner,
        mutateRecommendationBanner,
        serviceWithESimToActivateList,
        showBroadbandTechSupport,
        updateBackgroundHeight,
        updateBackgroundHeightFromElement,
    } = useHomePage();
    const { classes } = useStyle({ height: backgroundHeight });

    const history = useHistory();
    const [isSweeprWidgetVisible, setIsSweeprWidgetVisible] = useState(false);
    const closeSweeprWidget = useCallback(() => {
        setIsSweeprWidgetVisible(false);
    }, []);
    const [isRemoveRecommendationBannerModalOpen, setIsRemoveRecommendationBannerModalOpen] = useState(false);
    const [recommendationBannerTopOffset, setRecommendationBannerTopOffset] = useState<number>();
    const recommendationBannerRef = useRef<HTMLDivElement>(null);
    const { error, otcPayload, retryFetch } = useSweeprOtc({
        isEnabled: isSweeprWidgetVisible,
    });

    const navigateTo = (url?: string, externalUrl?: string) => {
        externalUrl ? (window.location.href = externalUrl) : history.push(url ?? "");
    };

    const handleMenuItemClick = (menuItem: IWelcomeMenuItem) => {
        if (menuItem.action === "openSweeprWidget") {
            setIsSweeprWidgetVisible(true);

            return;
        }

        navigateTo(menuItem.url, menuItem.externalUrl);
    };

    const { getIsFeatureEnabled } = useFeatureFlag();
    const isSweeprEnabled = getIsFeatureEnabled(featureFlagNameList.sweepr);

    const menuItemList: IWelcomeMenuItem[] = [
        {
            badge: count,
            icon: <AlertIcon />,
            label: t("navigation:myAlerts"),
            url: ALERTS_PAGE_URL,
        },
        {
            icon: <BillIcon />,
            label: t("navigation:myBills"),
            url: BILLS_PAGE_URL,
        },
        {
            icon: <UsageIcon />,
            label: t("navigation:myUsage"),
            url: USAGE_PAGE_URL,
        },
        {
            icon: <CardIcon />,
            label: t("navigation:myPayments"),
            url: PAYMENTS_PAGE_URL,
        },
        {
            icon: <AccountIcon />,
            label: t("navigation:accountDetails"),
            url: ACCOUNT_DETAILS_PAGE_URL,
        },
        {
            icon: <UserIcon />,
            label: t("navigation:myProfile"),
            url: PROFILE_PAGE_URL,
        },
        {
            externalUrl: `${WORDPRESS_URL}/contact`,
            icon: <ContactIcon />,
            label: t("footer:contactUs"),
        },
        {
            icon: <TravelIcon />,
            label: t("navigation:travelingOutsideEU"),
            externalUrl: GOMO_WORLD_URL,
        },
        ...(showBroadbandTechSupport && isSweeprEnabled
            ? [
                  {
                      action: "openSweeprWidget" as const,
                      icon: <BroadbandTechSupportIcon />,
                      label: t("navigation:broadbandTechSupport"),
                  },
              ]
            : []),
    ];

    const LabelTypography = (labelProps: { menuItem: IWelcomeMenuItem }) => {
        const label = (
            <Typography color="secondary" variant="large">
                {labelProps.menuItem.label}
            </Typography>
        );

        if (labelProps.menuItem.badge) {
            return (
                <Badge badgeContent={count} className="home-menu-content__badge" color="secondary">
                    {label}
                </Badge>
            );
        }

        return label;
    };

    return (
        <HomeContext.Provider value={{ updateBackgroundHeight }}>
            <div className="home" ref={homeRef}>
                <div className={classes.homeBackground} />
                <div className="container-boxed home-wrapper">
                    <Typography className="home-title" color="inherit" variant="h1">
                        {t("glossary:welcome")}
                    </Typography>
                    <HandleErrorAndLoading hideComponentWrapper isLoading={isLoading}>
                        {serviceWithESimToActivateList && serviceWithESimToActivateList.length > 0 && (
                            <ESimBanner serviceWithESimToActivateList={serviceWithESimToActivateList} />
                        )}
                        <Appointment appointmentList={appointmentList} mutate={mutate} />
                        {recommendationBanner && (
                            <div
                                className={classes.bannerContainer}
                                data-testid="recommendation-banner-container"
                                ref={recommendationBannerRef}
                            >
                                <IconButton
                                    aria-label={t("common:close")}
                                    className={classes.bannerCloseButton}
                                    size="large"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setRecommendationBannerTopOffset(
                                            recommendationBannerRef.current?.getBoundingClientRect().top
                                        );
                                        void setIsRemoveRecommendationBannerModalOpen(true);
                                    }}
                                >
                                    <SvgIcon className={classes.closeIcon} component={xHeader} viewBox="0 0 27 27" />
                                </IconButton>
                                <div
                                    dangerouslySetInnerHTML={{ __html: recommendationBanner }}
                                    ref={updateBackgroundHeightFromElement}
                                />
                            </div>
                        )}
                    </HandleErrorAndLoading>
                    {isRemoveRecommendationBannerModalOpen && (
                        <div
                            className={classes.recommendationBannerRemovalModal}
                            data-testid="remove-recommendation-banner-modal"
                        >
                            <RecommendationBannerRemovalModal
                                isRemoveRecommendationBannerModalOpen={isRemoveRecommendationBannerModalOpen}
                                mutateRecommendationBanner={mutateRecommendationBanner}
                                setIsRemoveRecommendationBannerModalOpen={setIsRemoveRecommendationBannerModalOpen}
                                topOffset={recommendationBannerTopOffset}
                            />
                        </div>
                    )}
                    <Card
                        className="home-menu"
                        elevation={2}
                        ref={recommendationBanner ? null : updateBackgroundHeightFromElement}
                    >
                        <CardContent className="home-menu-content">
                            <List component="nav">
                                <Grid container spacing={2} wrap="wrap">
                                    {menuItemList.map((menuItem) => (
                                        <Grid data-testid="menu-button" item key={menuItem.label} sm={6} xs={12}>
                                            <ListItemButton
                                                className="home-menu-content__navigation"
                                                onClick={() => handleMenuItemClick(menuItem)}
                                            >
                                                <ListItemIcon className="home-menu-content__icon">
                                                    {menuItem.icon}
                                                </ListItemIcon>
                                                <ListItemText primary={<LabelTypography menuItem={menuItem} />} />
                                                <KeyboardArrowRight className="home-menu-content__arrow" />
                                            </ListItemButton>
                                            <Divider variant="middle" />
                                        </Grid>
                                    ))}
                                </Grid>
                            </List>
                        </CardContent>
                    </Card>
                    {isSweeprWidgetVisible && (error || otcPayload) && (
                        <HandleErrorAndLoading error={error} isLoading={false} retryFetch={retryFetch}>
                            <SweeprWidgetContent otcPayload={otcPayload} onInteractionEnd={closeSweeprWidget} />
                        </HandleErrorAndLoading>
                    )}
                    <div className="home-orders">
                        <OrderHandler isHomePage />
                    </div>
                </div>
            </div>
        </HomeContext.Provider>
    );
};
